import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export interface AdminUser {
  id: string
  email: string
  name: string
  role: 'superuser' | 'admin' | 'employee' | 'tenant_admin' | 'consumer'
  tenant_id: string | null
  tenant_name: string | null
  tenant_slug: string | null
  is_active: boolean
  mfa_enabled: boolean
  feature_overrides: string[]
  created_at: string
  last_login: string | null
}

export interface UserFeature {
  key: string
  name: string
  description: string
  category: string
  has_access: boolean
  source: 'override' | 'role' | 'default' | 'none'
}

export interface FeatureDefinition {
  key: string
  name: string
  description: string
  category: string
  default_enabled: boolean
}

interface AdminUserListResponse {
  users: AdminUser[]
  total: number
  limit: number
  offset: number
}

export interface LoginHistoryEntry {
  id: string
  ip_address: string
  user_agent: string | null
  success: boolean
  login_method: string | null
  created_at: string
}

interface LoginHistoryResponse {
  entries: LoginHistoryEntry[]
  total: number
  limit: number
  offset: number
}

export interface UserUsageSummary {
  login_count_30d: number
  failed_login_count_30d: number
  last_login: string | null
  unique_ips_30d: number
  feature_count: number
  audit_actions_30d: number
  api_requests_30d: number | null
  api_success_rate: number | null
  avg_response_time_ms: number | null
  engage_sessions_30d: number | null
  engage_messages_30d: number | null
  voice_requests_30d: number | null
  voice_audio_seconds_30d: number | null
  extension_sessions_30d: number | null
  extension_turns_30d: number | null
  extension_nudges_30d: number | null
  consumer_utterances_30d: number | null
  consumer_distillations_30d: number | null
  consumer_insights_30d: number | null
}

export interface UsageTimeSeriesPoint {
  date: string
  logins?: number
  failed_logins?: number
  api_calls?: number
  engage_sessions?: number
  voice_requests?: number
  extension_sessions?: number
  consumer_utterances?: number
}

export interface UsageTimeSeriesResponse {
  period: string
  data_points: UsageTimeSeriesPoint[]
}

export interface EngageUsageResponse {
  total_sessions: number
  total_messages: number
  total_conditions_detected: number
  total_guidance_delivered: number
  avg_session_duration_seconds: number
  time_series: { date: string; sessions: number; messages: number }[]
}

export interface VoiceUsageResponse {
  total_requests: number
  total_audio_seconds: number
  total_cost_cents: number
  success_rate: number
  time_series: { date: string; requests: number; audio_seconds: number; cost_cents: number }[]
}

export interface ExtensionUsageResponse {
  total_sessions: number
  total_turns: number
  total_nudges: number
  nudge_acceptance_rate: number
  debriefs_generated: number
  submissions: number
  skill_growth: { module: string; signal: string; score: number; trend: string }[]
  time_series: { date: string; sessions: number; turns: number; nudges: number }[]
}

export interface UserDirectorySummary {
  total_users: number
  total_tenants: number
  consumers: number
  employees: number
  admins: number
  superusers: number
  tenant_admins: number
}

interface CreateUserRequest {
  email: string
  name: string
  password: string
  role: string
  tenant_id?: string
  is_active?: boolean
}

interface UpdateUserRequest {
  name?: string
  role?: string
  is_active?: boolean
  tenant_id?: string
  mfa_enabled?: boolean
}

interface UseAdminUsersOptions {
  role?: string
  tenant_id?: string
  search?: string
  is_active?: boolean
  limit?: number
  offset?: number
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  userSummary: ['admin-users-summary'],
  users: (options: UseAdminUsersOptions) => ['admin-users', options],
  user: (userId: string) => ['admin-user', userId],
  userFeatures: (userId: string) => ['admin-user-features', userId],
  featureRegistry: (category?: string) => ['admin-feature-registry', category],
  loginHistory: (userId: string, opts: object) => ['admin-user-login-history', userId, opts],
  userUsage: (userId: string) => ['admin-user-usage', userId],
  userUsageTimeSeries: (userId: string, period: string) => ['admin-user-usage-ts', userId, period],
  userEngageUsage: (userId: string) => ['admin-user-engage-usage', userId],
  userVoiceUsage: (userId: string) => ['admin-user-voice-usage', userId],
  userExtensionUsage: (userId: string) => ['admin-user-extension-usage', userId],
}

// =============================================================================
// USER QUERIES
// =============================================================================

/**
 * Fetch list of users for admin view
 */
export function useAdminUsers(options: UseAdminUsersOptions = {}) {
  const { role, tenant_id, search, is_active, limit = 50, offset = 0 } = options

  return useQuery<AdminUserListResponse>({
    queryKey: QUERY_KEYS.users({ role, tenant_id, search, is_active, limit, offset }),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (role) params.append('role', role)
      if (tenant_id) params.append('tenant_id', tenant_id)
      if (search) params.append('search', search)
      if (is_active !== undefined) params.append('is_active', String(is_active))
      params.append('limit', String(limit))
      params.append('offset', String(offset))

      const res = await api.get(`/admin/users?${params.toString()}`)
      return res.data
    },
  })
}

/**
 * Fetch single user details
 */
export function useAdminUser(userId: string | null) {
  return useQuery<AdminUser>({
    queryKey: QUERY_KEYS.user(userId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch user directory summary (aggregate counts)
 */
export function useUserDirectorySummary() {
  return useQuery<UserDirectorySummary>({
    queryKey: QUERY_KEYS.userSummary,
    queryFn: async () => {
      const res = await api.get('/admin/users/summary')
      return res.data
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  })
}

/**
 * Fetch user features with access status
 */
export function useUserFeatures(userId: string | null) {
  return useQuery<UserFeature[]>({
    queryKey: QUERY_KEYS.userFeatures(userId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}/features`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch feature registry (available features)
 */
export function useFeatureRegistry(category?: string) {
  return useQuery<FeatureDefinition[]>({
    queryKey: QUERY_KEYS.featureRegistry(category),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (category) params.append('category', category)

      const res = await api.get(`/admin/feature-registry?${params.toString()}`)
      return res.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - features don't change often
  })
}

// =============================================================================
// USER USAGE & HISTORY QUERIES
// =============================================================================

/**
 * Fetch login history for a user
 */
export function useUserLoginHistory(userId: string | null, options: { limit?: number; offset?: number } = {}) {
  const { limit = 20, offset = 0 } = options
  return useQuery<LoginHistoryResponse>({
    queryKey: QUERY_KEYS.loginHistory(userId || '', { limit, offset }),
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('limit', String(limit))
      params.append('offset', String(offset))
      const res = await api.get(`/admin/users/${userId}/login-history?${params.toString()}`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch user usage summary (30-day)
 */
export function useUserUsage(userId: string | null) {
  return useQuery<UserUsageSummary>({
    queryKey: QUERY_KEYS.userUsage(userId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}/usage`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch user usage time series
 */
export function useUserUsageTimeSeries(userId: string | null, period: string = '30d') {
  return useQuery<UsageTimeSeriesResponse>({
    queryKey: QUERY_KEYS.userUsageTimeSeries(userId || '', period),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}/usage/timeseries?period=${period}`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch user Engage usage details
 */
export function useUserEngageUsage(userId: string | null) {
  return useQuery<EngageUsageResponse>({
    queryKey: QUERY_KEYS.userEngageUsage(userId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}/usage/engage`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch user Voice usage details
 */
export function useUserVoiceUsage(userId: string | null) {
  return useQuery<VoiceUsageResponse>({
    queryKey: QUERY_KEYS.userVoiceUsage(userId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}/usage/voice`)
      return res.data
    },
    enabled: !!userId,
  })
}

/**
 * Fetch user Extension usage details
 */
export function useUserExtensionUsage(userId: string | null) {
  return useQuery<ExtensionUsageResponse>({
    queryKey: QUERY_KEYS.userExtensionUsage(userId || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/users/${userId}/usage/extension`)
      return res.data
    },
    enabled: !!userId,
  })
}

// =============================================================================
// FEATURE MUTATIONS
// =============================================================================

interface GrantFeaturesParams {
  userId: string
  features: string[]
}

/**
 * Grant features to a user
 */
export function useGrantFeatures() {
  const queryClient = useQueryClient()

  return useMutation<string[], Error, GrantFeaturesParams>({
    mutationFn: async ({ userId, features }) => {
      const res = await api.post(`/admin/users/${userId}/features`, { features })
      return res.data
    },
    onSuccess: (_, variables) => {
      // Invalidate user and feature queries
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(variables.userId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userFeatures(variables.userId) })
    },
  })
}

interface RevokeFeaturesParams {
  userId: string
  featureKey: string
}

/**
 * Revoke a feature from a user
 */
export function useRevokeFeature() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, RevokeFeaturesParams>({
    mutationFn: async ({ userId, featureKey }) => {
      await api.delete(`/admin/users/${userId}/features/${featureKey}`)
    },
    onSuccess: (_, variables) => {
      // Invalidate user and feature queries
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(variables.userId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userFeatures(variables.userId) })
    },
  })
}

// =============================================================================
// USER CRUD MUTATIONS
// =============================================================================

/**
 * Create a new user
 */
export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation<AdminUser, Error, CreateUserRequest>({
    mutationFn: async (data) => {
      const res = await api.post('/admin/users', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })
}

/**
 * Update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation<AdminUser, Error, { userId: string; data: UpdateUserRequest }>({
    mutationFn: async ({ userId, data }) => {
      const res = await api.patch(`/admin/users/${userId}`, data)
      return res.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(variables.userId) })
    },
  })
}

/**
 * Deactivate a user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: async (userId) => {
      await api.post(`/admin/users/${userId}/deactivate`)
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(userId) })
    },
  })
}

/**
 * Activate a user
 */
export function useActivateUser() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: async (userId) => {
      await api.post(`/admin/users/${userId}/activate`)
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(userId) })
    },
  })
}

/**
 * Change a user's role
 */
export function useChangeUserRole() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { userId: string; role: string }>({
    mutationFn: async ({ userId, role }) => {
      await api.post(`/admin/users/${userId}/role`, { role })
      return
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.user(variables.userId) })
    },
  })
}

// =============================================================================
// HELPER HOOKS
// =============================================================================

/**
 * Get employees only
 */
export function useAdminEmployees(options: Omit<UseAdminUsersOptions, 'role'> = {}) {
  return useAdminUsers({ ...options, role: 'employee' })
}

/**
 * Get tenant users only
 */
export function useAdminTenantUsers(options: Omit<UseAdminUsersOptions, 'role'> = {}) {
  return useAdminUsers({ ...options, role: 'tenant_admin' })
}
